import React from 'react';
import { Box, Button, styled, TableCell, TableRow, Typography } from '@mui/material';
import { InitialReviewProcedureItemProps } from './InitialReviewProcedureItem/InitialReviewProcedureItem';
import DecisionReasonSelect from '../DecisionReasonSelect';
import theme from '../../../theme';
import { CaseStages } from '../../../lib/constants/misc';
import { useScreenSmallerThan } from '../../../lib/hooks/breakpoint-hooks';
import {
    StyledKeyboardArrowUpOutlinedIcon,
    StyledKeyboardArrowDownIcon,
    KeyboardArrowContainer
} from '../../../styledComponents';
import { NormalBox } from './InitialReviewProcedureItem/StyledBoxCell';

export const TableDataCell = styled(TableCell)({
    borderBottom: 'none'
});
export const TypographyCell = styled(Typography)({
    fontWeight: 'bold',
    display: 'inline'
});

export const StyledButton = styled(Button)({
    borderRadius: '0.5rem',
    border: '0.063rem solid',
    width: '6.25rem',
    marginTop: '0.313rem',
    display: 'block'
});

const BoxRow = styled(Box)({
    display: 'flex',
    paddingTop: '0.625rem',
    justifyContent: 'space-between'
});

const InnerBox = styled(Box)({
    margin: '0.5rem 0'
});

const TypographyHeader = styled(Typography)({
    color: theme.palette.cardSubtitle.main,
    fontSize: 13,
    width: '50%'
});

export const UpheldStatus = {
    Overturn: 'Overturn',
    PartialOverturn: 'Partial Overturn',
    Upheld: 'Upheld',
    NoReviewRequired: 'No Review Required'
};

function AppealProcedureItem({
    item,
    reasons,
    caseStage,
    handleReasonDenialChange,
    setAnyChangeMade
}: InitialReviewProcedureItemProps) {
    const [upheldStatus, setUpheldStatus] = React.useState(item.isUpheld);
    const isClaimMedicalReview = caseStage === CaseStages.ClaimMedicalReview ? true : false;
    const showReconsiderationsDecision =
        caseStage === CaseStages.FirstLevelAppeal || caseStage === CaseStages.SecondLevelAppeal
            ? true
            : false;
    const showSecondaryDecisionReason = caseStage === CaseStages.ClaimMedicalReview ? true : false;
    const showFirstDecision = caseStage === CaseStages.SecondLevelAppeal ? true : false;
    const previousDecision = showFirstDecision
        ? 'Upheld'
        : item.overturnQuantity
        ? 'Overturn'
        : 'Upheld';
    const [approvedUnits, setapprovedUnits] = React.useState(
        item.approvedUnits ? item.approvedUnits : 0
    );
    const initialApproved = React.useRef<number>(approvedUnits);
    const [canIncrease, setCanIncrese] = React.useState(
        item.approvedUnits < item.requestedUnits &&
            (item.canAlterUpWards ||
                (!item.canAlterUpWards && item.approvedUnits < initialApproved.current))
    );
    const [canDecrease, setCanDecrese] = React.useState(
        item.approvedUnits > 0 &&
            (item.canAlterDownWards ||
                (!item.canAlterDownWards && approvedUnits > initialApproved.current))
    );

    const isSmallerThanSmall = useScreenSmallerThan('sm');
    const isSmallerThanLarge = useScreenSmallerThan(
        caseStage === CaseStages.FirstLevelAppeal ? 1090 : 1140
    );

    const StyledButtonUpheld = styled(StyledButton)({
        borderColor:
            upheldStatus === UpheldStatus.Upheld
                ? theme.palette.secondary.dark
                : theme.palette.secondary.main,
        color: upheldStatus === UpheldStatus.Upheld ? 'white' : theme.palette.cardSubtitle.main,
        background: upheldStatus === UpheldStatus.Upheld ? theme.palette.secondary.dark : 'white',
        '&:hover': {
            backgroundColor: theme.palette.secondary.dark,
            color: 'white'
        },
        ':disabled': {
            color: upheldStatus !== UpheldStatus.Upheld ? 'white' : theme.palette.cardSubtitle.main
        }
    });

    const StyledButtonParialOverturn = styled(StyledButton)({
        borderColor:
            upheldStatus === UpheldStatus.PartialOverturn
                ? theme.palette.secondary.dark
                : theme.palette.secondary.main,
        color:
            upheldStatus === UpheldStatus.PartialOverturn
                ? 'white'
                : theme.palette.cardSubtitle.main,
        background:
            upheldStatus === UpheldStatus.PartialOverturn ? theme.palette.secondary.dark : 'white',
        '&:hover': {
            backgroundColor: theme.palette.secondary.dark,
            color: 'white'
        },
        ':disabled': {
            color:
                upheldStatus !== UpheldStatus.PartialOverturn
                    ? 'white'
                    : theme.palette.cardSubtitle.main
        }
    });

    const StyledButtonOverturn = styled(StyledButton)({
        borderColor:
            upheldStatus === UpheldStatus.Overturn
                ? theme.palette.cardSubtitle.dark
                : theme.palette.secondary.main,
        color: upheldStatus === UpheldStatus.Overturn ? 'white' : theme.palette.cardSubtitle.main,
        background: upheldStatus === UpheldStatus.Overturn ? theme.palette.secondary.dark : 'white',
        '&:hover': {
            backgroundColor: theme.palette.secondary.dark,
            color: 'white'
        },
        ':disabled': {
            color:
                upheldStatus !== UpheldStatus.Overturn ? 'white' : theme.palette.cardSubtitle.main
        }
    });

    const StyledButtonNoReviewRequired = styled(StyledButton)({
        borderColor:
            upheldStatus === UpheldStatus.NoReviewRequired
                ? theme.palette.secondary.dark
                : theme.palette.secondary.main,
        color:
            upheldStatus === UpheldStatus.NoReviewRequired
                ? 'white'
                : theme.palette.cardSubtitle.main,
        background:
            upheldStatus === UpheldStatus.NoReviewRequired ? theme.palette.secondary.dark : 'white',
        '&:hover': {
            backgroundColor: theme.palette.secondary.dark,
            color: 'white'
        },
        ':disabled': {
            color:
                upheldStatus !== UpheldStatus.NoReviewRequired
                    ? 'white'
                    : theme.palette.cardSubtitle.main
        }
    });

    React.useEffect(() => {
        setapprovedUnits(approvedUnits);
        setCanIncrese(
            item.approvedUnits < item.requestedUnits &&
                (item.canAlterUpWards ||
                    (!item.canAlterUpWards && item.approvedUnits < initialApproved.current))
        );
        setCanDecrese(
            item.approvedUnits > 0 &&
                (item.canAlterDownWards ||
                    (!item.canAlterDownWards && item.approvedUnits > initialApproved.current))
        );
    }, [
        approvedUnits,
        item.deniedQuantity,
        item.approvedUnits,
        initialApproved,
        item.canAlterUpWards,
        item.requestedUnits,
        item.canAlterDownWards
    ]);

    const handleUpheld = () => {
        setAnyChangeMade(true);
        item.isUpheld = UpheldStatus.Upheld;
        setUpheldStatus(item.isUpheld);
        item.isNewCode = false;
    };

    const handleParialOverturn = () => {
        setAnyChangeMade(true);
        item.isUpheld = UpheldStatus.PartialOverturn;
        setUpheldStatus(item.isUpheld);
        item.isNewCode = false;
    };

    const handleOverturn = () => {
        setAnyChangeMade(true);
        item.isUpheld = UpheldStatus.Overturn;
        setUpheldStatus(item.isUpheld);
        item.isNewCode = false;
    };

    const handleNoReviewRequired = () => {
        setAnyChangeMade(true);
        item.isUpheld = UpheldStatus.NoReviewRequired;
        setUpheldStatus(item.isUpheld);
        item.isNewCode = false;
    };

    const increaseApproved = () => {
        setAnyChangeMade(true);
        if (item.approvedUnits == null) {
            item.approvedUnits = 0;
        }
        if (canIncrease) {
            item.approvedUnits++;
            setapprovedUnits(item.approvedUnits);
            item.deniedQuantity = item.requestedUnits - item.approvedUnits;
        }
    };
    const decreaseApproved = () => {
        setAnyChangeMade(true);
        if (item.approvedUnits == null) {
            item.approvedUnits = 0;
        }
        if (item.deniedQuantity == null) {
            item.deniedQuantity = item.requestedUnits;
        }
        if (canDecrease) {
            item.approvedUnits--;
            setapprovedUnits(item.approvedUnits);
            item.deniedQuantity = item.requestedUnits - item.approvedUnits;
        }
    };

    return !isSmallerThanSmall && !isSmallerThanLarge ? (
        <>
            <TableRow>
                <TableDataCell>
                    <TypographyCell>{item.requestedCode}</TypographyCell>
                </TableDataCell>
                <TableDataCell>
                    <Box sx={{ wordBreak: 'break-word' }}>{item.description}</Box>
                </TableDataCell>
                <TableDataCell sx={{ minWidth: '10rem', display: 'flex', flexDirection: 'column' }}>
                    <Box>
                        {' '}
                        <TypographyCell>Requested: </TypographyCell>
                        {item.requestedUnits}
                    </Box>
                    {item.requestedUnits >= 1 && (
                        <NormalBox sx={{ lineHeight: '2rem' }}>
                            <TypographyCell>Approved:</TypographyCell> {approvedUnits}
                            <KeyboardArrowContainer>
                                {canIncrease && (
                                    <StyledKeyboardArrowUpOutlinedIcon
                                        onClick={() =>
                                            increaseApproved()
                                        }></StyledKeyboardArrowUpOutlinedIcon>
                                )}
                                {canDecrease && (
                                    <StyledKeyboardArrowDownIcon
                                        onClick={() =>
                                            decreaseApproved()
                                        }></StyledKeyboardArrowDownIcon>
                                )}
                            </KeyboardArrowContainer>
                        </NormalBox>
                    )}
                </TableDataCell>
                <TableDataCell>
                    <Box>See nurse review above</Box>
                </TableDataCell>
                <TableDataCell>
                    <Box>{item.isApproved}</Box>
                </TableDataCell>
                <TableDataCell>
                    <Box>{item.initialDenialReason}</Box>
                </TableDataCell>
                {showReconsiderationsDecision ? (
                    <TableDataCell>
                        <Box>{previousDecision}</Box>
                    </TableDataCell>
                ) : null}
                {showFirstDecision ? (
                    <TableDataCell>
                        <Box>{item.overturnQuantity ? 'Overturn' : 'Upheld'}</Box>
                    </TableDataCell>
                ) : null}
                <TableDataCell>
                    <StyledButtonOverturn onClick={() => handleOverturn()}>
                        {isClaimMedicalReview ? 'Denied' : 'Overturn'}
                    </StyledButtonOverturn>
                    <StyledButtonParialOverturn onClick={() => handleParialOverturn()}>
                        {isClaimMedicalReview ? 'Partial Approval' : 'Partial Overturn'}
                    </StyledButtonParialOverturn>
                    <StyledButtonUpheld onClick={() => handleUpheld()}>
                        {isClaimMedicalReview ? 'Approved' : 'Upheld'}
                    </StyledButtonUpheld>
                    <StyledButtonNoReviewRequired onClick={() => handleNoReviewRequired()}>
                        No Review Required
                    </StyledButtonNoReviewRequired>
                </TableDataCell>

                <TableDataCell>
                    {!showReconsiderationsDecision && showSecondaryDecisionReason ? (
                        <Box sx={{ display: 'inline', borderColor: 'red' }}>
                            <DecisionReasonSelect
                                procedureId={item.crmCaseCodeId}
                                decisionReasons={item.decisionReasons}
                                reasons={reasons}
                                disabled={upheldStatus === UpheldStatus.Upheld}
                                required={
                                    upheldStatus
                                        ? upheldStatus !== UpheldStatus.Upheld &&
                                          upheldStatus !== UpheldStatus.NoReviewRequired &&
                                          item.decisionReasons &&
                                          item.decisionReasons.length <= 0
                                        : false
                                }
                                handleReasonDenialChange={handleReasonDenialChange}
                                approvalStatus={upheldStatus}></DecisionReasonSelect>
                        </Box>
                    ) : (
                        <Box></Box>
                    )}
                </TableDataCell>
            </TableRow>
        </>
    ) : isSmallerThanLarge && !isSmallerThanSmall ? (
        <>
            <BoxRow>
                <Box sx={{ margin: '0.5rem' }}>
                    <TypographyHeader>Requested Code</TypographyHeader>
                    <InnerBox>
                        <TypographyCell sx={{ width: '50%' }}>{item.requestedCode}</TypographyCell>
                    </InnerBox>
                </Box>
                <Box sx={{ margin: '0.5rem' }}>
                    <TypographyHeader>Description</TypographyHeader>
                    <InnerBox>
                        <Typography sx={{ width: '90%', wordBreak: 'break-word' }}>
                            {item.description}
                        </Typography>
                    </InnerBox>
                </Box>
                <Box minWidth={'120px'} sx={{ margin: '0.5rem' }}>
                    <TypographyHeader>Units</TypographyHeader>
                    <InnerBox sx={{ display: 'flex', flexDirection: 'column' }}>
                        <NormalBox>
                            <TypographyCell>Requested: </TypographyCell>
                            {item.requestedUnits}
                        </NormalBox>
                        {item.requestedUnits >= 1 && (
                            <NormalBox sx={{ lineHeight: '2rem' }}>
                                <TypographyCell>Approved:</TypographyCell> {approvedUnits}
                                <KeyboardArrowContainer>
                                    {canIncrease && (
                                        <StyledKeyboardArrowUpOutlinedIcon
                                            onClick={() =>
                                                increaseApproved()
                                            }></StyledKeyboardArrowUpOutlinedIcon>
                                    )}
                                    {canDecrease && (
                                        <StyledKeyboardArrowDownIcon
                                            onClick={() =>
                                                decreaseApproved()
                                            }></StyledKeyboardArrowDownIcon>
                                    )}
                                </KeyboardArrowContainer>
                            </NormalBox>
                        )}
                    </InnerBox>
                </Box>
                <Box sx={{ margin: '0.5rem' }}>
                    <TypographyHeader>Nurse Recommendation</TypographyHeader>
                    <InnerBox>
                        <Typography sx={{ width: '70%' }}>See nurse review above</Typography>
                    </InnerBox>
                </Box>
            </BoxRow>
            <BoxRow>
                <Box sx={{ margin: '0.5rem' }}>
                    <TypographyHeader>Initial Physician Decision</TypographyHeader>
                    <InnerBox sx={{ width: '50%' }}>{item.isApproved}</InnerBox>
                </Box>
                <Box sx={{ margin: '0.5rem' }}>
                    <TypographyHeader>Initial Denial Reason</TypographyHeader>
                    <InnerBox sx={{ width: '50%' }}>{item.initialDenialReason}</InnerBox>
                </Box>
                {showReconsiderationsDecision ? (
                    <Box sx={{ margin: '0.5rem' }}>
                        <TypographyHeader>Reconsiderations Decision</TypographyHeader>
                        <InnerBox sx={{ width: '50%' }}>
                            {item.overturnQuantity ? 'Overturn' : 'Upheld'}
                        </InnerBox>
                    </Box>
                ) : null}
                <Box sx={{ margin: '0.5rem' }}>
                    <TypographyHeader>Secondary Decision</TypographyHeader>
                    <InnerBox sx={{ width: '50%' }}>
                        <StyledButtonOverturn onClick={() => handleOverturn()}>
                            {isClaimMedicalReview ? 'Denied' : 'Overturn'}
                        </StyledButtonOverturn>
                        <StyledButtonParialOverturn onClick={() => handleParialOverturn()}>
                            {isClaimMedicalReview ? 'Partial Approval' : 'Partial Overturn'}
                        </StyledButtonParialOverturn>
                        <StyledButtonUpheld onClick={() => handleUpheld()}>
                            {isClaimMedicalReview ? 'Approved' : 'Upheld'}
                        </StyledButtonUpheld>
                        <StyledButtonNoReviewRequired onClick={() => handleNoReviewRequired()}>
                            No Review Required
                        </StyledButtonNoReviewRequired>
                    </InnerBox>
                </Box>
                {!showReconsiderationsDecision && showSecondaryDecisionReason ? (
                    <Box sx={{ margin: '0.5rem' }}>
                        <TypographyHeader>Secondary Decision Reason</TypographyHeader>
                        <InnerBox>
                            <Box
                                sx={{
                                    display: 'inline',
                                    borderColor: 'red',
                                    width: '50%',
                                    alignContent: 'center'
                                }}>
                                <DecisionReasonSelect
                                    procedureId={item.crmCaseCodeId}
                                    decisionReasons={item.decisionReasons}
                                    reasons={reasons}
                                    disabled={
                                        upheldStatus === UpheldStatus.Upheld ||
                                        upheldStatus === UpheldStatus.NoReviewRequired
                                    }
                                    required={
                                        upheldStatus
                                            ? upheldStatus !== UpheldStatus.Upheld &&
                                              upheldStatus !== UpheldStatus.NoReviewRequired &&
                                              item.decisionReasons &&
                                              item.decisionReasons.length <= 0
                                            : false
                                    }
                                    handleReasonDenialChange={handleReasonDenialChange}
                                    approvalStatus={upheldStatus}></DecisionReasonSelect>
                            </Box>
                        </InnerBox>
                    </Box>
                ) : null}
            </BoxRow>
        </>
    ) : (
        <>
            <BoxRow>
                <TypographyHeader>Requested Code</TypographyHeader>
                <TypographyHeader>Description</TypographyHeader>
            </BoxRow>
            <BoxRow>
                <TypographyCell sx={{ width: '50%' }}>{item.requestedCode}</TypographyCell>
                <Typography sx={{ width: '50%', wordBreak: 'break-word' }}>
                    {item.description}
                </Typography>
            </BoxRow>
            <BoxRow>
                <TypographyHeader>Units</TypographyHeader>
                <TypographyHeader>Nurse Recommendation</TypographyHeader>
            </BoxRow>
            <BoxRow>
                <Box sx={{ width: '50%', display: 'flex', flexDirection: 'column' }}>
                    <NormalBox>
                        <TypographyCell>Requested: </TypographyCell>
                        {item.requestedUnits}
                    </NormalBox>
                    {item.requestedUnits >= 1 && (
                        <NormalBox sx={{ lineHeight: '2rem' }}>
                            <TypographyCell>Approved:</TypographyCell> {approvedUnits}
                            <KeyboardArrowContainer>
                                {canIncrease && (
                                    <StyledKeyboardArrowUpOutlinedIcon
                                        onClick={() =>
                                            increaseApproved()
                                        }></StyledKeyboardArrowUpOutlinedIcon>
                                )}
                                {canDecrease && (
                                    <StyledKeyboardArrowDownIcon
                                        onClick={() =>
                                            decreaseApproved()
                                        }></StyledKeyboardArrowDownIcon>
                                )}
                            </KeyboardArrowContainer>
                        </NormalBox>
                    )}
                </Box>
                <Typography sx={{ width: '50%' }}>See nurse review above</Typography>
            </BoxRow>
            <BoxRow>
                <TypographyHeader>Initial Physician Decision</TypographyHeader>
                <TypographyHeader>Initial Denial Reason</TypographyHeader>
            </BoxRow>
            <BoxRow>
                <Box sx={{ width: '50%' }}>{item.isApproved}</Box>
                <Box sx={{ width: '50%' }}>{item.initialDenialReason}</Box>
            </BoxRow>
            {showReconsiderationsDecision ? (
                <Box>{item.overturnQuantity ? 'Overturn' : 'Upheld'}</Box>
            ) : null}
            <BoxRow>
                {showReconsiderationsDecision ? (
                    <TypographyHeader>Reconsiderations Decision</TypographyHeader>
                ) : null}
                <TypographyHeader>Secondary Decision</TypographyHeader>
            </BoxRow>
            <BoxRow>
                {showReconsiderationsDecision ? (
                    <Box sx={{ width: '50%' }}>{item.overturnQuantity ? 'Overturn' : 'Upheld'}</Box>
                ) : null}
                <Box sx={{ width: '50%' }}>
                    <StyledButtonOverturn onClick={() => handleOverturn()}>
                        {isClaimMedicalReview ? 'Denied' : 'Overturn'}
                    </StyledButtonOverturn>
                    <StyledButtonParialOverturn onClick={() => handleParialOverturn()}>
                        {isClaimMedicalReview ? 'Partial Approval' : 'Partial Overturn'}
                    </StyledButtonParialOverturn>
                    <StyledButtonUpheld onClick={() => handleUpheld()}>
                        {isClaimMedicalReview ? 'Approved' : 'Upheld'}
                    </StyledButtonUpheld>
                    <StyledButtonNoReviewRequired onClick={() => handleNoReviewRequired()}>
                        No Review Required
                    </StyledButtonNoReviewRequired>
                </Box>
            </BoxRow>
            <BoxRow>
                {!showReconsiderationsDecision && showSecondaryDecisionReason ? (
                    <TypographyHeader>Secondary Decision Reason</TypographyHeader>
                ) : null}
            </BoxRow>
            <BoxRow>
                {!showReconsiderationsDecision && showSecondaryDecisionReason ? (
                    <Box sx={{ display: 'inline', borderColor: 'red', width: '50%' }}>
                        <DecisionReasonSelect
                            procedureId={item.crmCaseCodeId}
                            decisionReasons={item.decisionReasons}
                            reasons={reasons}
                            disabled={
                                upheldStatus === UpheldStatus.Upheld ||
                                upheldStatus === UpheldStatus.NoReviewRequired
                            }
                            required={
                                upheldStatus
                                    ? upheldStatus !== UpheldStatus.Upheld &&
                                      upheldStatus !== UpheldStatus.NoReviewRequired &&
                                      item.decisionReasons &&
                                      item.decisionReasons.length <= 0
                                    : false
                            }
                            handleReasonDenialChange={handleReasonDenialChange}
                            approvalStatus={upheldStatus}></DecisionReasonSelect>
                    </Box>
                ) : (
                    <Box></Box>
                )}
            </BoxRow>
        </>
    );
}
export default AppealProcedureItem;
